import { useCallback, useEffect, useRef, useState } from 'react';
import type { PluginListenerHandle } from '@capacitor/core';
import { androidApkUrl, fetchAndroidRelease } from '../api/client.ts';
import {
  AppUpdate,
  dismissedVersion,
  hasNativeUpdate,
  parseRelease,
  rememberDismissed,
  shouldOffer,
  type AndroidRelease,
} from './nativeUpdate.ts';

/**
 * Where in the update the banner is. One control does all of it, and this is
 * what the control says.
 *
 * `ready` is a downloaded file that has not been installed — the system dialog
 * was dismissed, or the owner was sent to the «неизвестные источники» switch and
 * has come back. Pressing again from there must not download eleven megabytes a
 * second time, which is why the state exists at all rather than collapsing into
 * `offer`.
 */
export type UpdateStage = 'offer' | 'downloading' | 'ready' | 'failed';

export interface AndroidUpdate {
  /** The build being offered, or null when there is nothing to offer. */
  release: AndroidRelease | null;
  stage: UpdateStage;
  /** 0…1 while downloading, null when the server sent no length. */
  progress: number | null;
  /** Why the last attempt did not end in an installer. */
  message: string | null;
  /** The banner's one button: download, install, or try again. */
  act: () => void;
  dismiss: () => void;
}

/**
 * A minute, not the hour appUpdate.ts keeps for the web. The web can afford to
 * be slow: the service worker applies a new build silently and the next launch
 * simply has it. An APK needs its owner to press a button, so the banner being
 * an hour late is the fix being an hour late — and what the check costs is a
 * conditional GET of a two-hundred-byte manifest, only on launch, on return to
 * the app and on opening a conversation, never on a timer.
 */
const CHECK_EVERY_MS = 60_000;

/** A fragment from the plugin, as the first word of a line on the banner. */
function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const IDLE: AndroidUpdate = {
  release: null,
  stage: 'offer',
  progress: null,
  message: null,
  act: () => {},
  dismiss: () => {},
};

/**
 * Offers the newer APK, when there is one and this is the Android app.
 *
 * The check runs on launch, on every return to the app and on every opened
 * conversation (`openedChatId` — the caller's current chat, whose changes are
 * the «I am using this right now» signal), at most once a minute — an APK has
 * no store behind it, so a build that fixes something reaches a phone only if
 * the phone asks.
 *
 * Everywhere else this returns a constant and touches nothing. The web has the
 * service worker (appUpdate.ts) and iOS has TestFlight.
 */
export function useAndroidUpdate(openedChatId?: string | null): AndroidUpdate {
  const [release, setRelease] = useState<AndroidRelease | null>(null);
  const [stage, setStage] = useState<UpdateStage>('offer');
  const [progress, setProgress] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  /** The downloaded file, kept so a second press installs rather than refetches. */
  const downloaded = useRef<string | null>(null);
  const installed = useRef<number | null>(null);
  /** Which build is on the banner, in a form the checking effect can read. */
  const offered = useRef<number | null>(null);
  const busy = useRef(false);
  /** The main effect's check, reachable from the chat-open effect below it. */
  const checkRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!hasNativeUpdate) return;
    let alive = true;
    let lastCheckedAt = 0;

    async function check(): Promise<void> {
      const now = Date.now();
      if (now - lastCheckedAt < CHECK_EVERY_MS) return;
      lastCheckedAt = now;

      try {
        if (installed.current === null) {
          const { versionCode } = await AppUpdate.current();
          installed.current = versionCode;
        }
        const found = parseRelease(await fetchAndroidRelease());
        if (!alive) return;
        if (!shouldOffer(found, installed.current, dismissedVersion())) return;
        // A newer build arriving while an older one sits downloaded: the file
        // on disk is the wrong one now, so it is forgotten rather than
        // installed. Nothing deletes it — the cache directory is Android's to
        // sweep, and the next download overwrites it.
        if (found !== null && found.versionCode !== offered.current) {
          offered.current = found.versionCode;
          downloaded.current = null;
          setStage('offer');
          setMessage(null);
        }
        setRelease(found);
      } catch {
        // Offline, or a server mid-deploy. Nothing is shown and nothing is
        // reported: an update check is the one request whose failure is
        // ordinary, and the next return to the app tries again.
      }
    }

    void check();
    checkRef.current = () => void check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      checkRef.current = null;
      document.removeEventListener('visibilitychange', onVisible);
    };
    // Once, for the lifetime of the screen: the minute clock above lives in
    // this closure, and a re-run would restart it. Which build is being offered
    // is read through a ref for exactly that reason.
  }, []);

  // Opening a conversation asks again, through the same throttle. Through a
  // ref rather than by depending on the chat id in the effect above, which
  // would tear the clock down and rebuild it on every navigation.
  useEffect(() => {
    if (openedChatId != null) checkRef.current?.();
  }, [openedChatId]);

  const act = useCallback(() => {
    if (release === null || busy.current) return;
    busy.current = true;
    setMessage(null);

    void (async () => {
      let listener: PluginListenerHandle | null = null;
      try {
        if (downloaded.current === null) {
          setStage('downloading');
          setProgress(0);
          listener = await AppUpdate.addListener('updateProgress', ({ loaded, total }) => {
            setProgress(total > 0 ? Math.min(1, loaded / total) : null);
          });
          const { path } = await AppUpdate.download({
            url: androidApkUrl(release.file),
            sha256: release.sha256,
          });
          downloaded.current = path;
        }

        setStage('ready');
        const { needsPermission } = await AppUpdate.install({ path: downloaded.current });
        if (needsPermission) {
          // The settings screen is already open in front of them; this is the
          // sentence they read on the way back.
          setMessage(
            'Разрешите установку из этого источника и нажмите «Установить» ещё раз.',
          );
        }
      } catch (error) {
        setStage('failed');
        // The plugin's own words — «нет сети», «сервер ответил 502», «файл
        // повреждён при загрузке» are three different next steps. They are
        // written as fragments, and this is the one place they become a line
        // somebody reads.
        setMessage(sentence(error instanceof Error ? error.message : 'не удалось обновить'));
      } finally {
        void listener?.remove();
        setProgress(null);
        busy.current = false;
      }
    })();
  }, [release]);

  const dismiss = useCallback(() => {
    if (release !== null) rememberDismissed(release.versionCode);
    setRelease(null);
  }, [release]);

  if (!hasNativeUpdate) return IDLE;
  return { release, stage, progress, message, act, dismiss };
}
